'use client';

import { useAtomValue } from 'jotai';
import Cookies from 'js-cookie';

import { PLP_FILTERS_STATE } from '@cookies/Constants/Cookie.constants';
import { previousPageTypeAtom } from '@core/Atoms/PageTypeAtom';
import { postcodeAtom } from '@core/Atoms/Postcode/Postcode.atom';
import { PAGE_TYPE } from '@core/Constants/PageType';
import type { Locale } from '@core/Entities/Locale/Locale.entity';
import type { FilterParams } from '@core/Entities/Search/QueryParams.entity';
import type { Make } from '@core/Entities/Vehicle/Make.entity';
import type { Model } from '@core/Entities/Vehicle/Model.entity';
import { getModelNameFromId } from '@core/Services/Search/Utils/GetModelNameFromId';
import { safeJsonParse } from '@core/Utils/Json/SafeJsonParse';
import { Breadcrumbs } from '@gds/Breadcrumbs/Breadcrumbs';
import useBreakpoint from '@gds/Pagination/Hooks/UseBreakpoint.hook';
import { BackToPlp } from '@shared-components/BackToPlp/BackToPlp';

import styles from './PdpBreadcrumb.module.css';

interface PdpBreadcrumbProps {
  strings: Record<string, string>;
  locale: Locale;
  make: Make;
  model: Model;
}

const PdpBreadcrumb: React.FC<PdpBreadcrumbProps> = ({
  strings,
  locale,
  make,
  model,
}: PdpBreadcrumbProps) => {
  const pageType = useAtomValue(previousPageTypeAtom);
  const { backBrdcmbText, homeBrdcmbText, allCarsBrdcmbText, allLeaseBrdcmbText } = strings;

  const decodedValue = decodeURIComponent(Cookies.get(PLP_FILTERS_STATE) ?? '');
  const postcodeValue = useAtomValue(postcodeAtom);
  const filterParams: FilterParams = safeJsonParse(decodedValue, {});
  const isLeasingPage = pageType === PAGE_TYPE.Leasing || pageType === PAGE_TYPE.LeasingPlp;
  const isFilteredPlp = pageType === PAGE_TYPE.Plp || isLeasingPage;

  const modelSlug = getModelNameFromId(model.id);

  const { postcode = postcodeValue[locale], lat, lon } = filterParams || {};

  const postcodeSlug = postcode ? `postcode=${postcode}` : null;
  const latSlug = lat ? `&lat=${lat}` : null;
  const lonSlug = lon ? `&lon=${lon}` : null;

  const breadcrumbs = [
    { link: '/', title: homeBrdcmbText, hide: true },
    ...(isLeasingPage
      ? [
          {
            link: `/cars/lease-deals`,
            title: allLeaseBrdcmbText,
            hide: true,
          },
        ]
      : [
          {
            link: `/cars/used/?${[postcodeSlug, latSlug, lonSlug].filter(Boolean).join('&')}`,
            title: allCarsBrdcmbText,
            hide: true,
          },
        ]),
    {
      link: `/cars/used/${make.id}/?${[postcodeSlug, latSlug, lonSlug].filter(Boolean).join('&')}`,
      title: `${make.label}`,
      hide: false,
    },
    {
      link: `/cars/used/${make.id}/${modelSlug}/?${[postcodeSlug, latSlug, lonSlug]
        .filter(Boolean)
        .join('&')}`,
      title: `${model.label}`,
      hide: false,
    },
  ];

  const {
    breakpoints: { isMobile },
  } = useBreakpoint();

  return (
    <div className={styles.container} data-test-id="pdp-breadcrumb-container">
      {isFilteredPlp ? (
        <BackToPlp
          buttonText={isMobile ? '' : backBrdcmbText}
          className={styles.backButton}
          locale={locale}
          pageType={pageType}
          make={make}
          model={model}
          data-pdp-breadcrumb-v3
          size="small"
        />
      ) : null}
      <Breadcrumbs
        breadcrumbs={breadcrumbs}
        allowLastItemAsLink
        data-pdp-breadcrumb
        allowToggleHidden={false}
        hasCustomShowHide
      />
    </div>
  );
};

export default PdpBreadcrumb;
